'use client';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  faArrowRight,
  faChevronLeft,
  faChevronRight,
  faUsersMedical,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  AddToListModal,
  Card,
  HorizontalScroller,
  ProfileCardHeader,
  PulseButton,
  PulseIconButton,
  Tooltip,
} from 'src/components';
import type { ProviderItem } from 'src/components/AddToListModal';
import type { HorizontalScrollerHandle } from 'src/components/PulseUI/HorizontalScroller';
import { useGetProviderNetworkQuery } from 'src/api/redux';
import { useLogging } from 'src/context/logging';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { CardLoadingIndicator } from '../Card';
import { NoDataAvailable } from '../NoData';
import { SimilarProviderCard } from './components';
import {
  CAROUSEL_SCROLL_AMOUNT,
  CONNECTION_META,
  LEGEND_CONNECTION_TYPES,
  PROVIDER_NETWORK_MAX_PAGE_SIZE,
  PROVIDER_NETWORK_PAGE_SIZE,
} from './constants';
import type { SimilarProvider } from './types';
import { toSimilarProviders } from './utils';

/**
 * Related Providers carousel — providers who share patients, training, a group or site
 * of care, a society, or industry ties with the profile being viewed, ordered by
 * connection strength.
 *
 * The endpoint returns raw connection edges; the presentation fields each card renders
 * are derived in `utils.ts` (see `toSimilarProviders`).
 */
const ProfileSimilarProviders = () => {
  const { entityDetails } = useProfile();
  const log = useLogging();
  const scrollerRef = useRef<HorizontalScrollerHandle>(null);

  const [providerToAdd, setProviderToAdd] = useState<SimilarProvider | null>(
    null
  );
  const [pageSize, setPageSize] = useState(PROVIDER_NETWORK_PAGE_SIZE);

  const providerId = entityDetails?.provider_id || entityDetails?.id;

  const { data, currentData, isLoading, isFetching, refetch } =
    useGetProviderNetworkQuery(
      { providerId: String(providerId), pageSize },
      { skip: !providerId }
    );

  // The "same specialty" reason compares each card against the profile, so the adapter
  // runs here rather than in the endpoint's transformResponse.
  const similarProviders = useMemo(
    () => toSimilarProviders(data?.results, entityDetails?.specialty),
    [data?.results, entityDetails?.specialty]
  );

  const hasRequestedMax = pageSize >= PROVIDER_NETWORK_MAX_PAGE_SIZE;

  /**
   * Whether to offer "Load more". The first page coming back full is the only signal
   * that the server may be holding more — a short response is everything there is.
   *
   * The second half deliberately tests `currentData` (the response for the *current*
   * arg) rather than the card count. On click `pageSize` jumps to the maximum while
   * `data` still holds the previous arg's smaller page, so a count comparison would
   * unmount the control the instant it was pressed — hiding its pending state and
   * leaving no way to retry a failed expansion. `currentData` only fills in once the
   * larger response actually lands.
   */
  const canLoadMore =
    similarProviders.length >= PROVIDER_NETWORK_PAGE_SIZE &&
    !(hasRequestedMax && !!currentData);

  const handleLoadMore = useCallback(() => {
    // Already at the maximum: the control is only still here because the expansion
    // failed, so this is a retry rather than a wider request.
    if (hasRequestedMax) {
      refetch();
      return;
    }

    setPageSize(PROVIDER_NETWORK_MAX_PAGE_SIZE);
    log.event('Loaded More Similar Providers', {
      source: 'ProfileSimilarProviders',
      providerId,
      pageSize: PROVIDER_NETWORK_MAX_PAGE_SIZE,
    });
  }, [hasRequestedMax, refetch, log, providerId]);

  const handleAddToList = useCallback((provider: SimilarProvider) => {
    setProviderToAdd(provider);
  }, []);

  const handleAddToListSuccess = useCallback(
    (result: { addedCount: number }) => {
      log.event('addProvider', {
        source: 'ProfileSimilarProviders',
        count: result?.addedCount,
        type: 'single',
      });
      setProviderToAdd(null);
    },
    [log]
  );

  // Send only what the modal needs to resolve the record
  const providersToAdd = useMemo<ProviderItem[]>(
    () =>
      providerToAdd
        ? [
            {
              provider_id: providerToAdd.provider_id,
              type: providerToAdd.type || 'hcp',
            },
          ]
        : [],
    [providerToAdd]
  );

  return (
    <Card
      className="p-6 bg-white flex-col"
      data-testid="profile-similar-providers"
    >
      <div className="flex items-start justify-between gap-4">
        <ProfileCardHeader
          icon={faUsersMedical}
          title="Related Providers"
          subHeader={`Top 50 providers who share patients or attributes with ${
            entityDetails?.name || 'this provider'
          } — ranked by connection strength.`}
        />
        <div className="flex flex-none gap-2">
          <PulseIconButton
            icon={faChevronLeft}
            variant="outline"
            color="neutral"
            size="sm"
            onClick={() => scrollerRef.current?.scrollBy('left')}
            aria-label="Scroll to previous related providers"
            data-testid="similar-providers-scroll-previous"
          />
          <PulseIconButton
            icon={faChevronRight}
            variant="outline"
            color="neutral"
            size="sm"
            onClick={() => scrollerRef.current?.scrollBy('right')}
            aria-label="Scroll to next related providers"
            data-testid="similar-providers-scroll-next"
          />
        </div>
      </div>

      <div
        className="flex flex-wrap gap-4 pb-4"
        data-testid="similar-providers-legend"
      >
        {LEGEND_CONNECTION_TYPES.map((connectionType) => {
          const meta = CONNECTION_META[connectionType];

          // Focusable so keyboard users get the tooltip too (it opens on focus); the
          // sr-only copy is what screen readers hear, since the tooltip is portaled.
          return (
            <Tooltip
              key={connectionType}
              content={meta.description}
              slotProps={{ contentClassName: 'max-w-64' }}
            >
              <span
                tabIndex={0}
                className="flex items-center gap-1.5 text-xs text-tertiary cursor-help"
                data-testid={`similar-providers-legend-${connectionType}`}
              >
                <Icon
                  icon={meta.icon}
                  className={`w-3 h-3 rounded-sm ${meta.pillClassName}`}
                  aria-hidden="true"
                />
                {meta.label}
                <span className="sr-only">: {meta.description}</span>
              </span>
            </Tooltip>
          );
        })}
      </div>

      <div className="relative min-h-[5rem]">
        {/*
          Only the first load blanks the carousel. Expanding via "Load more" refetches
          with a larger pageSize, and RTK Query keeps the previous arg's `data` while it
          runs — so the cards stay put and the button carries the pending state.
        */}
        {isLoading && <CardLoadingIndicator />}

        {similarProviders.length > 0 ? (
          <HorizontalScroller
            ref={scrollerRef}
            showArrows={false}
            scrollAmount={CAROUSEL_SCROLL_AMOUNT}
            className="gap-4 py-1"
            testIdPrefix="similar-providers-scroller"
          >
            {similarProviders.map((provider) => (
              <SimilarProviderCard
                key={provider?.provider_id}
                provider={provider}
                onAddToList={handleAddToList}
              />
            ))}
            {canLoadMore && (
              <div className="flex flex-none items-center justify-center w-40">
                <PulseButton
                  variant="outline"
                  color="neutral"
                  size="sm"
                  endDecorator={faArrowRight}
                  loading={isFetching}
                  onClick={handleLoadMore}
                  aria-label="Load more related providers"
                  data-testid="similar-providers-load-more"
                >
                  Load more
                </PulseButton>
              </div>
            )}
          </HorizontalScroller>
        ) : (
          !isLoading && <NoDataAvailable />
        )}
      </div>

      <AddToListModal
        isOpen={!!providerToAdd}
        onClose={() => setProviderToAdd(null)}
        mode="add"
        providers={providersToAdd}
        onSuccess={handleAddToListSuccess}
      />
    </Card>
  );
};

export default ProfileSimilarProviders;
